import React, { useCallback, useEffect, useRef, useState } from "react";
import ChatMarkdown, { markdownToHtml } from "./ChatMarkdown";
import { sendContactEmail } from "../utils/sendContactEmail";
import {
  trackContactFormSubmit,
  trackContactFormSuccess,
  trackContactFormError,
} from "../utils/analytics";
import {
  EMPTY_LEAD,
  shouldStartLead,
  isCancel,
  isAffirmation,
  isValidEmail,
  nextMissingStep,
  promptForStep,
  absorbReply,
  buildLeadEmailBody,
} from "../utils/chatLeadCapture";
import "./ChatWidget.scss";

const LAUNCHER_LABEL = "Chat with Hiren’s AI";
const PANEL_TITLE = "Hiren’s AI assistant";

const SUGGESTIONS = [
  "What services do you offer?",
  "Tell me about your React Native work",
  "Ballpark quote for a mobile app?",
  "I’d like Hiren to contact me",
];

const NEAR_BOTTOM_PX = 120;
const GATED_THINK_MIN_MS = 1200;
const GATED_THINK_MAX_MS = 2200;
const GATED_TOKEN_GAP_MS = 18;

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function gatedThinkMs() {
  return (
    GATED_THINK_MIN_MS +
    Math.floor(Math.random() * (GATED_THINK_MAX_MS - GATED_THINK_MIN_MS + 1))
  );
}

async function readSseStream(res, onEvent) {
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const parts = buffer.split("\n\n");
    buffer = parts.pop() || "";

    for (const part of parts) {
      const lines = part.split("\n");
      for (const line of lines) {
        if (!line.startsWith("data:")) continue;
        const raw = line.slice(5).trim();
        if (!raw) continue;
        try {
          await onEvent(JSON.parse(raw));
        } catch {
          // ignore malformed chunk
        }
      }
    }
  }
}

function ChatLauncherIcon({ open }) {
  if (open) {
    return (
      <svg
        className="chat-widget__launcher-svg"
        viewBox="0 0 24 24"
        width="22"
        height="22"
        aria-hidden="true"
        focusable="false"
      >
        <path
          d="M6.5 6.5l11 11M17.5 6.5l-11 11"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.2"
          strokeLinecap="round"
        />
      </svg>
    );
  }

  // Chat bubble + AI spark — reads as “assistant”, not a generic emoji
  return (
    <svg
      className="chat-widget__launcher-svg"
      viewBox="0 0 24 24"
      width="22"
      height="22"
      aria-hidden="true"
      focusable="false"
    >
      <path
        d="M4.5 16.2V7.8A2.8 2.8 0 0 1 7.3 5h9.4A2.8 2.8 0 0 1 19.5 7.8v5.2a2.8 2.8 0 0 1-2.8 2.8H9.2L4.5 19v-2.8z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
      <circle cx="9" cy="10.4" r="1.05" fill="currentColor" />
      <circle cx="12.2" cy="10.4" r="1.05" fill="currentColor" />
      <circle cx="15.4" cy="10.4" r="1.05" fill="currentColor" />
      <path
        d="M18.2 3.2l.35 1.05L19.6 4.6l-1.05.35-.35 1.05-.35-1.05-1.05-.35 1.05-.35.35-1.05z"
        fill="currentColor"
      />
    </svg>
  );
}

function isNearBottom(el) {
  if (!el) return true;
  return el.scrollHeight - el.scrollTop - el.clientHeight <= NEAR_BOTTOM_PX;
}

function ChatWidget({ onOpenContact }) {
  const [available, setAvailable] = useState(false);
  const [checked, setChecked] = useState(false);
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [streaming, setStreaming] = useState(false);
  const [leadActive, setLeadActive] = useState(false);
  const [leadStep, setLeadStep] = useState(null);
  const [lead, setLead] = useState(EMPTY_LEAD);
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content:
        "Hi! Looking to build a **web app**, **mobile app**, or need a **ballpark quote**? Ask me about Hiren’s work — happy to help you figure out fit, timeline, and next steps.",
    },
  ]);

  const listRef = useRef(null);
  const inputRef = useRef(null);
  const streamNodeRef = useRef(null);
  const abortRef = useRef(null);
  const stickToBottomRef = useRef(true);
  const streamTextRef = useRef("");
  const unlockUntilRef = useRef(0);
  const finishedRef = useRef(false);
  const messagesRef = useRef(messages);
  const leadRef = useRef(lead);
  const leadStepRef = useRef(leadStep);
  const leadActiveRef = useRef(leadActive);
  const leadSendingRef = useRef(false);
  const leadSentEmailRef = useRef("");
  const pendingLeadRef = useRef(null); // keep name+email after a failed send for retry

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);
  useEffect(() => {
    leadRef.current = lead;
  }, [lead]);
  useEffect(() => {
    leadStepRef.current = leadStep;
  }, [leadStep]);
  useEffect(() => {
    leadActiveRef.current = leadActive;
  }, [leadActive]);

  const scrollToBottom = useCallback(() => {
    const el = listRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, []);

  const maybeFollowBottom = useCallback(() => {
    if (Date.now() < unlockUntilRef.current) return;
    if (!stickToBottomRef.current) return;
    scrollToBottom();
  }, [scrollToBottom]);

  const unlockFollow = useCallback(() => {
    stickToBottomRef.current = false;
    unlockUntilRef.current = Date.now() + 900;
  }, []);

  const onMessagesScroll = useCallback(() => {
    const el = listRef.current;
    if (!el) return;
    if (Date.now() < unlockUntilRef.current) {
      stickToBottomRef.current = false;
      return;
    }
    stickToBottomRef.current = isNearBottom(el);
  }, []);

  useEffect(() => {
    const el = listRef.current;
    if (!open || !el) return undefined;

    const onWheel = (e) => {
      if (e.deltaY < 0 || !isNearBottom(el)) unlockFollow();
    };
    const onTouchMove = () => unlockFollow();

    el.addEventListener("wheel", onWheel, { passive: true });
    el.addEventListener("touchmove", onTouchMove, { passive: true });
    return () => {
      el.removeEventListener("wheel", onWheel);
      el.removeEventListener("touchmove", onTouchMove);
    };
  }, [open, unlockFollow, streaming]);

  const paintStreamText = useCallback(() => {
    const node = streamNodeRef.current;
    if (node) {
      node.innerHTML = markdownToHtml(streamTextRef.current) || "";
    }
    maybeFollowBottom();
  }, [maybeFollowBottom]);

  const checkHealth = useCallback(async () => {
    try {
      const res = await fetch("/api/chat", { method: "GET" });
      if (!res.ok) {
        setAvailable(false);
        return;
      }
      const data = await res.json();
      setAvailable(Boolean(data.available));
    } catch {
      setAvailable(false);
    } finally {
      setChecked(true);
    }
  }, []);

  useEffect(() => {
    checkHealth();
    const id = setInterval(checkHealth, 45_000);
    return () => clearInterval(id);
  }, [checkHealth]);

  useEffect(() => {
    document.body.classList.toggle("chat-assistant-online", available);
    return () => document.body.classList.remove("chat-assistant-online");
  }, [available]);

  useEffect(() => {
    document.body.classList.toggle("chat-assistant-open", open && available);
    return () => document.body.classList.remove("chat-assistant-open");
  }, [open, available]);

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
    if (open) {
      stickToBottomRef.current = true;
      unlockUntilRef.current = 0;
      requestAnimationFrame(scrollToBottom);
    }
  }, [open, scrollToBottom]);

  const pushAssistant = useCallback(
    (content) => {
      setMessages((prev) => [...prev, { role: "assistant", content }]);
      requestAnimationFrame(scrollToBottom);
    },
    [scrollToBottom]
  );

  const resetLead = useCallback(() => {
    // Clear state + refs immediately so “Yes” can’t re-trigger send
    leadActiveRef.current = false;
    leadStepRef.current = null;
    leadRef.current = EMPTY_LEAD;
    leadSendingRef.current = false;
    setLeadActive(false);
    setLeadStep(null);
    setLead(EMPTY_LEAD);
  }, []);

  const submitLead = useCallback(
    async (finalLead) => {
      const emailKey = finalLead.email.trim().toLowerCase();
      if (leadSendingRef.current) return false;
      if (leadSentEmailRef.current && leadSentEmailRef.current === emailKey) {
        pendingLeadRef.current = null;
        resetLead();
        pushAssistant(
          "Your details are already with Hiren. What else would you like to know about his work?"
        );
        return false;
      }

      leadSendingRef.current = true;
      // Leave lead mode before the network call so follow-ups like “Yes” go to normal chat
      leadActiveRef.current = false;
      leadStepRef.current = null;
      setLeadActive(false);
      setLeadStep(null);

      const payload = {
        name: finalLead.name.trim(),
        email: finalLead.email.trim(),
        phone: "",
        company: "",
        message: buildLeadEmailBody(finalLead, messagesRef.current),
        source: "chat-widget-lead",
      };

      trackContactFormSubmit(payload);
      pushAssistant("Got it — sending your details to Hiren…");

      const result = await sendContactEmail(payload);
      leadSendingRef.current = false;

      if (result.success) {
        leadSentEmailRef.current = emailKey;
        pendingLeadRef.current = null;
        leadRef.current = EMPTY_LEAD;
        setLead(EMPTY_LEAD);
        trackContactFormSuccess();
        pushAssistant(
          `Done, **${finalLead.name}** — Hiren will follow up at **${finalLead.email}** soon.\n\nWant to keep chatting?`
        );
        return true;
      }

      // Keep details for retry — don’t make them re-type name/email
      pendingLeadRef.current = {
        name: finalLead.name.trim(),
        email: finalLead.email.trim(),
      };
      leadRef.current = EMPTY_LEAD;
      setLead(EMPTY_LEAD);
      trackContactFormError(result.error || "Email send failed");
      pushAssistant(
        `Sorry — I couldn’t send that just now. Your name and email are saved here — say **try again** to resend, or use **Let’s Connect**. Meanwhile, ask me anything about Hiren’s work or a quote.`
      );
      return false;
    },
    [pushAssistant, resetLead]
  );

  const continueLead = useCallback(
    async (incomingLead) => {
      let current = { ...incomingLead };

      if (current.email && !isValidEmail(current.email)) {
        current = { ...current, email: "" };
        leadRef.current = current;
        leadStepRef.current = "email";
        leadActiveRef.current = true;
        setLead(current);
        setLeadStep("email");
        setLeadActive(true);
        pushAssistant(
          "That email doesn’t look valid — what’s the best **email** to reach you?"
        );
        return;
      }

      const step = nextMissingStep(current);

      // Name + email ready → send once (project = chat summary)
      if (!step) {
        leadRef.current = current;
        setLead(current);
        await submitLead(current);
        return;
      }

      leadRef.current = current;
      leadStepRef.current = step;
      leadActiveRef.current = true;
      setLead(current);
      setLeadActive(true);
      setLeadStep(step);
      pushAssistant(promptForStep(step, current));
    },
    [pushAssistant, submitLead]
  );

  const startLeadFlow = useCallback(
    (seedText = "") => {
      // Allow a fresh contact attempt
      leadSentEmailRef.current = "";
      leadActiveRef.current = true;
      setLeadActive(true);
      let draft = { ...EMPTY_LEAD };
      if (seedText) {
        draft = absorbReply(draft, "name", seedText);
        if (shouldStartLead(seedText) && draft.name && shouldStartLead(draft.name)) {
          draft = { ...draft, name: "" };
        }
      }
      continueLead(draft);
    },
    [continueLead]
  );

  const handleLeadReply = useCallback(
    async (text) => {
      if (isCancel(text)) {
        resetLead();
        pushAssistant(
          "No problem — cancelled. Keep asking about Hiren’s work, or say **I’d like Hiren to contact me** anytime."
        );
        return;
      }

      // “Yes” after “Want to keep chatting?” (or mid-flow) → normal chat, never resend
      if (isAffirmation(text)) {
        resetLead();
        pushAssistant(
          "Great — ask me anything about Hiren’s projects, stack, timelines, or a ballpark quote."
        );
        return;
      }

      const missing = nextMissingStep(leadRef.current);
      const step = leadStepRef.current || missing;
      if (!step) {
        // Already have name+email — do not treat random replies as a resend
        resetLead();
        pushAssistant(
          "Your details are with Hiren. What would you like to know next?"
        );
        return;
      }

      const updated = absorbReply(leadRef.current, step, text);

      if (step === "name" && !updated.name) {
        leadRef.current = updated;
        setLead(updated);
        pushAssistant("What **name** should Hiren use when he replies?");
        return;
      }
      if (step === "email" && !isValidEmail(updated.email)) {
        leadRef.current = updated;
        setLead(updated);
        pushAssistant(
          "I still need a valid **email** (like name@company.com). What’s the best one?"
        );
        return;
      }

      await continueLead(updated);
    },
    [continueLead, pushAssistant, resetLead]
  );

  const sendMessage = async (text) => {
    const message = (text || "").trim();
    if (!message || busy) return;

    setMessages((prev) => [...prev, { role: "user", content: message }]);
    setInput("");
    requestAnimationFrame(scrollToBottom);

    // Retry failed EmailJS send without re-asking name/email
    if (
      pendingLeadRef.current &&
      /^(try again|retry|resend|send again)$/i.test(message)
    ) {
      const pending = pendingLeadRef.current;
      setBusy(true);
      await submitLead(pending);
      setBusy(false);
      return;
    }

    // Conversational lead capture (in-chat only)
    if (leadActiveRef.current) {
      setBusy(true);
      await handleLeadReply(message);
      setBusy(false);
      return;
    }

    if (shouldStartLead(message)) {
      setBusy(true);
      // If we already collected details earlier this session, just resend
      if (pendingLeadRef.current?.name && pendingLeadRef.current?.email) {
        await submitLead(pendingLeadRef.current);
      } else {
        startLeadFlow(message);
      }
      setBusy(false);
      return;
    }

    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    const history = messagesRef.current
      .filter((m) => m.role === "user" || m.role === "assistant")
      .slice(-8)
      .map((m) => ({ role: m.role, content: m.content }));

    stickToBottomRef.current = true;
    unlockUntilRef.current = 0;
    streamTextRef.current = "";
    finishedRef.current = false;

    setBusy(true);
    setStreaming(true);

    requestAnimationFrame(() => {
      paintStreamText();
      scrollToBottom();
    });

    const finishAssistant = (content) => {
      if (finishedRef.current) return;
      finishedRef.current = true;
      const finalText = (content || "").trim() || "No response.";
      streamTextRef.current = "";
      setStreaming(false);
      setMessages((prev) => [...prev, { role: "assistant", content: finalText }]);
      requestAnimationFrame(maybeFollowBottom);
    };

    try {
      const res = await fetch("/api/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "text/event-stream" },
        body: JSON.stringify({ message, history }),
        signal: controller.signal,
      });

      const contentType = res.headers.get("content-type") || "";

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        finishAssistant(
          data.error ||
            "Sorry — the assistant is offline right now. Try again when Hiren’s local model is running, or use Let’s Connect."
        );
        if (res.status === 503) setAvailable(false);
        return;
      }

      if (contentType.includes("text/event-stream") && res.body) {
        let gated = false;
        let thinkUntil = 0;
        let pending = "";

        const flushPending = async () => {
          if (!pending) return;
          if (thinkUntil) {
            const wait = thinkUntil - Date.now();
            if (wait > 0) await sleep(wait);
            thinkUntil = 0;
          }
          if (gated && pending.length > 12) {
            const burst = pending;
            pending = "";
            for (let i = 0; i < burst.length; i += 3) {
              if (finishedRef.current || controller.signal.aborted) return;
              streamTextRef.current += burst.slice(i, i + 3);
              paintStreamText();
              if (i + 3 < burst.length) await sleep(GATED_TOKEN_GAP_MS);
            }
            return;
          }
          streamTextRef.current += pending;
          pending = "";
          paintStreamText();
        };

        await readSseStream(res, async (event) => {
          if (finishedRef.current) return;
          if (event.type === "start" && event.gated) {
            gated = true;
            thinkUntil = Date.now() + gatedThinkMs();
            return;
          }
          if (event.type === "token" && event.content) {
            pending += event.content;
            await flushPending();
          } else if (event.type === "done") {
            await flushPending();
            finishAssistant(event.reply || streamTextRef.current);
          } else if (event.type === "error") {
            finishAssistant(event.error || "Stream interrupted.");
          }
        });
        if (!finishedRef.current) {
          await flushPending();
          finishAssistant(streamTextRef.current || "No response.");
        }
      } else {
        const data = await res.json().catch(() => ({}));
        finishAssistant(data.reply || data.error || "No response.");
      }
    } catch (err) {
      if (err.name === "AbortError") {
        finishedRef.current = true;
        setStreaming(false);
        return;
      }
      finishAssistant(
        "Could not reach the assistant. Check that Ollama (and ngrok, if used) are running."
      );
      setAvailable(false);
    } finally {
      setBusy(false);
      abortRef.current = null;
    }
  };

  const onSubmit = (e) => {
    e.preventDefault();
    sendMessage(input);
  };

  if (!checked || !available) return null;

  const placeholder = leadActive
    ? leadStep === "email"
      ? "Your email…"
      : "Your full name…"
    : "Ask about Hiren, a quote, or say contact me…";

  return (
    <div className={`chat-widget ${open ? "chat-widget--open" : ""}`}>
      {open && (
        <div className="chat-widget__panel" role="dialog" aria-label={PANEL_TITLE}>
          <div className="chat-widget__header">
            <div className="chat-widget__brand">
              <span className="chat-widget__avatar" aria-hidden="true">
                HS
              </span>
              <div>
                <strong>{PANEL_TITLE}</strong>
                <p>
                  <span className="chat-widget__live" />{" "}
                  {leadActive
                    ? "Just need name & email…"
                    : "Online · quotes, projects & connect"}
                </p>
              </div>
            </div>
            <button
              type="button"
              className="chat-widget__icon-btn"
              onClick={() => setOpen(false)}
              aria-label="Close chat"
            >
              ×
            </button>
          </div>

          <div
            className="chat-widget__messages"
            ref={listRef}
            onScroll={onMessagesScroll}
          >
            {messages.map((m, i) => (
              <div
                key={`${m.role}-${i}`}
                className={`chat-widget__bubble chat-widget__bubble--${m.role}`}
              >
                {m.role === "assistant" ? (
                  <ChatMarkdown content={m.content} />
                ) : (
                  m.content
                )}
              </div>
            ))}

            {streaming && (
              <div className="chat-widget__bubble chat-widget__bubble--assistant chat-widget__bubble--streaming">
                <div ref={streamNodeRef} className="chat-md chat-stream-text" />
                <span className="chat-widget__typing" aria-hidden="true">
                  <i />
                  <i />
                  <i />
                </span>
              </div>
            )}
          </div>

          {!busy && !leadActive && (
            <div className="chat-widget__suggestions">
              {SUGGESTIONS.map((s) => (
                <button key={s} type="button" onClick={() => sendMessage(s)}>
                  {s}
                </button>
              ))}
            </div>
          )}

          <form className="chat-widget__form" onSubmit={onSubmit}>
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={placeholder}
              maxLength={800}
              disabled={busy}
              aria-label="Chat message"
            />
            <button type="submit" disabled={busy || !input.trim()}>
              {busy ? "…" : "Send"}
            </button>
          </form>

          {typeof onOpenContact === "function" && (
            <button
              type="button"
              className="chat-widget__contact"
              onClick={() => {
                setOpen(false);
                onOpenContact();
              }}
            >
              Prefer the full form? Let’s Connect →
            </button>
          )}
        </div>
      )}

      <button
        type="button"
        className={`chat-widget__launcher ${open ? "is-open" : "is-pulse"}`}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-label={open ? "Close chat" : LAUNCHER_LABEL}
      >
        <span className="chat-widget__launcher-icon" aria-hidden="true">
          <ChatLauncherIcon open={open} />
        </span>
        <span className="chat-widget__launcher-text">
          {open ? "Close chat" : LAUNCHER_LABEL}
        </span>
        {!open && <span className="chat-widget__badge">AI</span>}
      </button>
    </div>
  );
}

export default ChatWidget;
